import React, { useEffect, useState } from "react";
import { api, INTEREST_LABELS } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Send, Loader2, Mail, Sparkles, Wand2 } from "lucide-react";

const AI_MODELS = [
  { value: "anthropic:claude-sonnet-5-5", label: "Claude Sonnet 5.5", provider: "anthropic", model: "claude-sonnet-5-5" },
  { value: "openai:gpt-6-sol", label: "GPT 6 Sol", provider: "openai", model: "gpt-6-sol" },
];

const SEGMENTS = [
  { value: "all", label: "Minden feliratkozó" },
  { value: "marketing", label: "Csak marketing-hozzájárulással" },
  { value: "csak_erdekel", label: `Csak "${INTEREST_LABELS.csak_erdekel}"` },
  { value: "1_zsak", label: `Csak "${INTEREST_LABELS["1_zsak"]}"` },
  { value: "2_3_zsak", label: `Csak "${INTEREST_LABELS["2_3_zsak"]}"` },
  { value: "4_10_zsak", label: `Csak "${INTEREST_LABELS["4_10_zsak"]}"` },
  { value: "10plusz_zsak", label: `Csak "${INTEREST_LABELS["10plusz_zsak"]}"` },
];

export default function AdminNewsletter() {
  const [form, setForm] = useState({ name: "", subject: "", message: "", segment: "marketing" });
  const [testEmail, setTestEmail] = useState("");
  const [recipientCount, setRecipientCount] = useState(null);
  const [sending, setSending] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [bump, setBump] = useState(0);
  const [aiModel, setAiModel] = useState("anthropic:claude-sonnet-5-5");
  const [aiTopic, setAiTopic] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.post("/admin/newsletter/preview", form)
      .then((r) => setRecipientCount(r.data.recipient_count))
      .catch(() => setRecipientCount(0));
  }, [form.segment]);

  useEffect(() => {
    api.get("/admin/campaigns").then((r) => setCampaigns(r.data.campaigns || []));
  }, [bump]);

  const sendTest = async () => {
    if (!testEmail) return toast.error("Add meg a teszt email címet");
    if (!form.subject.trim() || !form.message.trim()) return toast.error("Töltsd ki a tárgyat és az üzenetet");
    setSending(true);
    try {
      await api.post("/admin/newsletter/send", { ...form, name: form.name || "Teszt", test_email: testEmail });
      toast.success(`Teszt email elküldve: ${testEmail}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nem sikerült elküldeni");
    } finally {
      setSending(false);
    }
  };

  const sendCampaign = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.message.trim()) {
      return toast.error("Töltsd ki az összes mezőt");
    }
    if (!window.confirm(`Biztosan elküldöd? ${recipientCount || 0} címzett.`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/newsletter/send", form);
      toast.success(`Kampány elküldve: ${data.sent} fő / ${data.recipient_count}`);
      setForm({ name: "", subject: "", message: "", segment: "marketing" });
      setBump((b) => b + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nem sikerült elküldeni");
    } finally {
      setSending(false);
    }
  };

  const generateAiDraft = async () => {
    if (!aiTopic.trim()) return toast.error("Add meg a téma rövid leírását");
    const [provider, model] = aiModel.split(":");
    setAiLoading(true);
    try {
      const { data } = await api.post("/admin/newsletter/ai-draft", {
        provider,
        model,
        topic: aiTopic,
        segment: form.segment,
      });
      setForm((f) => ({ ...f, subject: data.subject, message: data.message }));
      toast.success(`AI vázlat elkészült (${data.model})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nem sikerült AI vázlatot generálni");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <p className="section-label">Hírlevél</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#201B18] mt-1">Új kampány</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl bg-white border border-[#2C221E]/10 p-6 space-y-5">
          <div className="rounded-2xl bg-gradient-to-br from-[#201B18] to-[#2C221E] text-[#FBF8F3] p-5 border border-[#C29B78]/30">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-[#C29B78]" />
              <p className="font-mono-brand text-[11px] tracking-[0.25em] text-[#C29B78]">AI VÁZLAT · CLAUDE + GPT</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Select value={aiModel} onValueChange={setAiModel}>
                <SelectTrigger data-testid="newsletter-ai-model-select" className="bg-white/5 border-white/10 text-[#FBF8F3]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AI_MODELS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input
                value={aiTopic}
                onChange={(e) => setAiTopic(e.target.value)}
                placeholder="Téma, pl. 'Első adag hamarosan'"
                data-testid="newsletter-ai-topic-input"
                className="bg-white/5 border-white/10 text-[#FBF8F3] placeholder:text-[#8C8075]"
              />
            </div>
            <Button
              onClick={generateAiDraft}
              disabled={aiLoading}
              data-testid="newsletter-ai-generate-button"
              className="w-full h-10 mt-3 rounded-full bg-[#C29B78] hover:bg-[#9E7A5A] text-[#2C221E] font-semibold"
            >
              {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wand2 className="h-4 w-4 mr-2" /> AI vázlat generálása</>}
            </Button>
            <p className="mt-2 text-[11px] text-[#8C8075]">A generált tárgy és üzenet beíródik a mezőkbe – kézzel finomíthatod küldés előtt.</p>
          </div>

          <div className="space-y-2">
            <Label>Kampány neve (belső)</Label>
            <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="pl. Első adag bejelentés" data-testid="newsletter-name-input" />
          </div>
          <div className="space-y-2">
            <Label>Tárgy</Label>
            <Input value={form.subject} onChange={(e) => update("subject", e.target.value)} placeholder="pl. Elkészült az első GrowBag 🌱" data-testid="newsletter-subject-input" />
          </div>
          <div className="space-y-2">
            <Label>Üzenet</Label>
            <Textarea rows={8} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Szia! Az első GrowBag adag elkészült..." data-testid="newsletter-message-input" />
          </div>
          <div className="space-y-2">
            <Label>Címzettek</Label>
            <Select value={form.segment} onValueChange={(v) => update("segment", v)}>
              <SelectTrigger data-testid="newsletter-segment-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SEGMENTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-[#8C8075]">Címzettek száma: <strong className="text-[#2C221E] font-mono-brand">{recipientCount ?? "–"}</strong></p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl bg-[#F3EDE2] border border-[#C29B78]/30 p-5">
            <p className="section-label mb-3">Teszt küldés</p>
            <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="te@email.hu" data-testid="newsletter-test-email-input" className="mb-3" />
            <button onClick={sendTest} disabled={sending} data-testid="newsletter-test-send-button" className="btn-secondary w-full">
              <Mail className="h-4 w-4" /> Teszt email
            </button>
          </div>

          <div className="rounded-2xl bg-[#2C221E] text-[#FBF8F3] p-5">
            <p className="section-label text-[#C29B78] mb-3">Küldés</p>
            <p className="text-sm text-[#D7CFC6] mb-4">Minden marketing email tartalmaz leiratkozási lehetőséget.</p>
            <Button onClick={sendCampaign} disabled={sending || !recipientCount} data-testid="admin-newsletter-send-button" className="w-full h-11 bg-[#FBF8F3] text-[#2C221E] hover:bg-[#F3EDE2] rounded-full">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Küldés most</>}
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-[#2C221E]/10 overflow-hidden">
        <div className="p-5">
          <p className="section-label">Korábbi kampányok</p>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Név</TableHead>
                <TableHead>Tárgy</TableHead>
                <TableHead>Szegmens</TableHead>
                <TableHead>Elküldve</TableHead>
                <TableHead>Dátum</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-[#8C8075] py-8">Még nincs kampány.</TableCell></TableRow>
              ) : (
                campaigns.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-[#5C524A]">{c.subject}</TableCell>
                    <TableCell className="font-mono-brand text-xs">{c.segment}</TableCell>
                    <TableCell>{c.sent} / {c.recipient_count}</TableCell>
                    <TableCell className="text-xs text-[#8C8075]">{new Date(c.created_at).toLocaleString("hu-HU")}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
